/** Pure presentation helpers for the metrics-expansion cards (RPI, growth, contribution, concentration, rating, adoption). */
import type { Mover, Rpi, VersionAdoption } from "@apphq/shared/types";
import { eur, int, pct } from "./format";

export type SignedTone = "up" | "down" | "flat" | "none";

const MINUS = "−";

/** Fractional change -> `+12,5%` / `−3%` / `0%`; null / non-finite -> `—` (no base, never a fake 0). */
export function signedPct(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return "—";
  if (Math.abs(ratio) < 0.0005) return "0%";
  return `${ratio > 0 ? "+" : MINUS}${pct(Math.abs(ratio))}`;
}

export function signedTone(ratio: number | null | undefined): SignedTone {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return "none";
  if (Math.abs(ratio) < 0.0005) return "flat";
  return ratio > 0 ? "up" : "down";
}

export interface RpiView {
  /** "value" = a real per-install figure, "no_data" = no install data (yet), "sandbox" = production-only metric. */
  state: "value" | "no_data" | "sandbox";
  value: string;
  hint: string;
}

/** Revenue per install as a EUR string; a positive amount below one cent is shown as `< € 0,01`, never as a raw 0. */
export function rpiEur(minor: number): string {
  if (minor > 0 && minor < 1) return "< € 0,01";
  return eur(minor);
}

/** Display state of the RPI KPI. No install data (pipeline fills 24-48h after activation) is not the same as `€ 0 / install`. */
export function rpiView(rpi: Rpi | null | undefined, env: "production" | "sandbox"): RpiView {
  if (env === "sandbox") return { state: "sandbox", value: "—", hint: "alleen productie" };
  if (!rpi || rpi.install_source === "none" || rpi.installs === null || rpi.installs === undefined) {
    return { state: "no_data", value: "—", hint: "nog geen installdata" };
  }
  if (rpi.revenue_per_install === null || !Number.isFinite(rpi.revenue_per_install)) {
    return { state: "no_data", value: "—", hint: "geen installs in deze periode" };
  }
  const proceeds = rpi.proceeds_per_install !== null && Number.isFinite(rpi.proceeds_per_install) ? ` · netto ${rpiEur(rpi.proceeds_per_install)}` : "";
  return { state: "value", value: rpiEur(rpi.revenue_per_install), hint: `per install · ${int(rpi.installs)} installs${proceeds}` };
}

export interface RatingView {
  has: boolean;
  /** `★★★★☆` rounded to whole stars. */
  stars: string;
  avg: string;
  count: string;
}

/** Stars + average + count; `has=false` when Apple returned no rating yet (never `0 sterren`). */
export function ratingView(avg: number | null | undefined, count: number | null | undefined): RatingView {
  if (avg === null || avg === undefined || !Number.isFinite(avg) || avg < 1 || avg > 5) return { has: false, stars: "", avg: "—", count: "—" };
  const full = Math.max(0, Math.min(5, Math.round(avg)));
  return {
    has: true,
    stars: "★".repeat(full) + "☆".repeat(5 - full),
    avg: avg.toLocaleString("nl-NL", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    count: count === null || count === undefined || !Number.isFinite(count) ? "—" : int(count),
  };
}

/** Compact `★ 4,3 (1.234)` for table rows / lists; `—` without a rating. */
export function ratingShort(r: { avg: number | null; count: number | null } | null | undefined): string {
  const v = ratingView(r?.avg, r?.count);
  if (!v.has) return "—";
  return v.count === "—" ? `★ ${v.avg}` : `★ ${v.avg} (${v.count})`;
}

/** "62% op laatste versie (2.4.1)" or null without data. Uses the API-computed adoption (never the truncated top-8 split). */
export function adoptionText(a: VersionAdoption | null | undefined): string | null {
  if (!a || !Number.isFinite(a.share)) return null;
  return `${pct(a.share)} op laatste versie (${a.latest})`;
}

/** Whale shares: null (fewer than 10 paying customers) means "too few", not 0%. */
export function whaleCell(share: number | null | undefined): string {
  return share === null || share === undefined || !Number.isFinite(share) ? "—" : pct(share);
}

export function whalesAvailable(w: { top1: number | null; top5: number | null; top10: number | null }): boolean {
  return w.top1 !== null || w.top5 !== null || w.top10 !== null;
}

/** All four movers null (fewer than 2 apps or no base) -> hide the block. */
export function moversEmpty(m: { revenue: { up: Mover; down: Mover }; dau: { up: Mover; down: Mover } }): boolean {
  return !m.revenue.up && !m.revenue.down && !m.dau.up && !m.dau.down;
}

/** Label for a share, rounded exactly like the bar width so label and bar never disagree. */
export function shareLabel(share: number): string {
  return `${sharePercent(share)}%`;
}

/** Bar width 0-100 for a 0..1 share. */
export function sharePercent(share: number): number {
  if (!Number.isFinite(share) || share <= 0) return 0;
  return Math.min(100, Math.round(share * 100));
}
